import {
    AlertTitle,
    Box,
    Container,
    HStack,
    useDisclosure,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { BiBlock } from 'react-icons/bi';
import { LiaHashtagSolid } from 'react-icons/lia';
import {
    MdAddCall,
    MdDialpad,
    MdPhonelinkRing,
    MdViewModule,
} from 'react-icons/md';

import { Alert, AlertIcon, Text } from '@/components/v4';
import { getAvailNumberblocks } from 'API/Numbers/getAvailNumberblocks';
import {
    useAssociatedAccounts,
    useDashboardServices,
} from 'hooks/useCustomQuery';
import { useTpmContext } from 'pages/tpm';
import { useGetCountriesList } from 'pages/telephony/numbers/queries/useGetCountriesList';

import NumberTable from '@/pages/telephony/numbers/edit/Index';
import Platforms, { PlatformEnums } from '@/utils/platforms';
import useGetOperatorPlatform from 'pages/telephony/numbers/queries/useGetOperatorPlatform';
import { useGetData } from '@/utils/queryUtils';
import AssociatedAccountDrawer from 'pages/tpm/AssociatedAccounts/AssociatedAccountDrawer';
import { AiOutlineCheckCircle, AiOutlineInfoCircle } from 'react-icons/ai';

import ScopeType from 'utils/enums/ScopeType';

import AvailableNumberBlocksDrawer from './drawers/AvailableNumberBlocksDrawer';

import { initialState, path } from '@/pages/tpm/constants';
import { SET_TPM_COUNTRIES } from '@/store/constants/service';

import SummaryPill from 'components/v4/Pills/SummaryPill';
import ExistingNumbersDrawer from './drawers/ExistingNumbersDrawer';
import RequestBlockDrawer from './drawers/RequestBlockDrawer';

// Every platform the local map can name. Operator platforms are left out: they
// are branded per company, so their name comes from the service profile.
const LOCAL_PLATFORMS = Object.values(PlatformEnums)
    .map((id) => ({ id, name: Platforms[id]?.name }))
    .filter((platform) => platform.name);

function Numbers() {
    const {
        tpmNumber: [, setTpmState],
    } = useTpmContext();
    const { currentCompany, currentCompanyPlatforms, currentCompanyData } =
        useSelector((state) => state.navigationLists);
    const { currentScope } = useSelector((state) => {
        return { ...state.login };
    });

    const dispatch = useDispatch();
    const location = useLocation();
    const hasAutoOpenedRef = useRef(false);
    const navigationFrom = location.state?.from;
    const { darkMode } = useSelector((state) => {
        return {
            darkMode: state.settings.darkMode,
            colorScheme: state.settings?.colorScheme,
        };
    });
    const [tpmService, setTpmService] = useState(null);
    const [tableData, setTableData] = useState([]);
    const [summaryData, setSummaryData] = useState([]);

    // Partner scope has no platforms endpoint of its own yet, so its filter is
    // built from the locally known platforms plus the operator platform, which
    // is the only one the local map cannot name. Company scope keeps using the
    // company platforms endpoint, which already returns the accurate list.
    const isPartnerScope = currentScope === ScopeType.PARTNER;

    const { operatorPlatform, isLoading: isLoadingOperatorPlatform } =
        useGetOperatorPlatform(currentCompany, isPartnerScope);

    const platformList = useMemo(() => {
        if (isPartnerScope) {
            if (isLoadingOperatorPlatform) return undefined;
            return operatorPlatform
                ? [...LOCAL_PLATFORMS, operatorPlatform]
                : LOCAL_PLATFORMS;
        }

        if (!currentCompanyPlatforms) return undefined;
        const filteredPlatforms = currentCompanyPlatforms.filter(
            (platform, index, self) =>
                index === self.findIndex((p) => p.id === platform.id),
        );
        return filteredPlatforms.map((platform) => ({
            id: platform.id,
            name: Platforms[platform.id]?.name || platform.name,
        }));
    }, [
        isPartnerScope,
        isLoadingOperatorPlatform,
        operatorPlatform,
        currentCompanyPlatforms,
    ]);

    const expandedPlatformNumberIdRef = useRef();
    const expandedPlatformNumbersRef = useRef();

    const { currentStep, countries: countryList } = useSelector(
        (state) => state.service.tpm,
    );

    const {
        isOpen: isTPMOpen,
        onOpen: onTPMOpen,
        onClose: onTPMClose,
    } = useDisclosure();

    const {
        isOpen: isAvailableNumberBlocksOpen,
        onOpen: onAvailableNumberBlocksOpen,
        onClose: onAvailableNumberBlocksClose,
    } = useDisclosure();
    const {
        isOpen: isRequestNumbersOpen,
        onOpen: onRequestNumbersOpen,
        onClose: onRequestNumbersClose,
    } = useDisclosure();
    const {
        isOpen: isBringExistingNumbersOpen,
        onOpen: onBringExistingNumbersOpen,
        onClose: onBringExistingNumbersClose,
    } = useDisclosure();

    const { data: availableServices } = useDashboardServices({
        currentCompany,
        options: {
            cacheTime: 1,
        },
    });

    const {
        data: associatedAccounts,
        isLoading,
        isFetching,
    } = useAssociatedAccounts(tpmService ? tpmService.id : null);

    const { data: countries, isFetching: isLoadingCountries } =
        useGetCountriesList({
            currentCompany,
        });
    const qk = 'getAvailNumberblocks';

    const {
        // data,
        status: queryStatus,
        isRefetching,
        refetch,
    } = useGetData({
        queryKey: [qk],
        fetchFunction: getAvailNumberblocks,
        fetchFunctionParams: [currentCompany],
        setData: setTableData,
        enabled: !!currentCompany,
    });

    const handleAssociationButton = useCallback(() => {
        onTPMOpen();
        setTpmState(initialState);
        const processedCountries = countries?.map((c) =>
            c?.['alpha2']?.toLowerCase(),
        );
        if (
            processedCountries instanceof Array &&
            !Array.isArray(countryList)
        ) {
            dispatch({
                type: SET_TPM_COUNTRIES,
                payload: processedCountries,
            });
        }
    }, [onTPMOpen, setTpmState, dispatch, countries, countryList]);

    const availableNumberBlocksDisabled =
        currentScope === ScopeType.PARTNER ||
        currentScope === ScopeType.WHOLESALER ||
        tableData?.length <= 0 ||
        currentCompanyData?.approved === false;

    const menuData = useMemo(() => {
        let menuItems = [
            {
                icon: MdAddCall,
                title: 'Request New Numbers',
                action: onRequestNumbersOpen,
                description: 'Place an order for new Telephone Numbers',
            },
            {
                icon: MdDialpad,
                title: 'Bring Existing Numbers',
                action: onBringExistingNumbersOpen,
                description: 'Initiate a Number Port request',
            },
        ];
        if (!availableNumberBlocksDisabled) {
            menuItems.unshift({
                icon: MdViewModule,
                title: 'Available Number Blocks',
                action: onAvailableNumberBlocksOpen,
                description:
                    'Search for and add Telephone Numbers directly to your company',
            });
        }
        if (tpmService?.id) {
            menuItems.unshift({
                icon: MdPhonelinkRing,
                title: 'Mobile Numbers',
                action: handleAssociationButton,
                description: 'Setup and configure Teams Phone Mobile',
            });
        }

        return menuItems;
    }, [
        onRequestNumbersOpen,
        onBringExistingNumbersOpen,
        availableNumberBlocksDisabled,
        tpmService?.id,
        onAvailableNumberBlocksOpen,
        handleAssociationButton,
    ]);

    const numberBlocksTableProps = {
        data: tableData,
        queryStatus,
        isRefetching,
        refetch,
        qk,
        countries,
    };

    useEffect(() => {
        if (availableServices) {
            setTpmService(
                availableServices.find((s) => s.name === 'Teams Phone Mobile'),
            );

            if (tpmService) {
                dispatch({
                    type: 'CHANGE_MODAL_STATE',
                    payload: {
                        service: tpmService,
                    },
                });
            }
        }
    }, [availableServices, dispatch, tpmService]);

    // Auto-open TPM drawer when navigating from TPM card at dashboard
    useEffect(() => {
        if (
            !hasAutoOpenedRef.current &&
            tpmService?.id &&
            countries &&
            typeof navigationFrom === 'string' &&
            navigationFrom === path.DASHBOARD_SERVICES
        ) {
            hasAutoOpenedRef.current = true;
            handleAssociationButton();
        }
    }, [tpmService, navigationFrom, countries, handleAssociationButton]);

    if (!currentCompany) {
        return (
            <Alert status="error">
                <AlertIcon />
                <AlertTitle>
                    No Partner / Company / Wholesaler Selected
                </AlertTitle>
                <Text>Please select a wholesaler and a partner / company.</Text>
            </Alert>
        );
    }

    // Counts are per number in the general list and per platform number in the
    // platform view, so the labels name neither — each pill counts whatever the
    // table below it is showing.
    const summaryDetails = [
        {
            icon: LiaHashtagSolid,
            count: summaryData?.numbers,
            toolTip: 'Total Numbers',
        },
        {
            icon: AiOutlineCheckCircle,
            count: summaryData?.activePlatformNumbers,
            toolTip: 'Active Numbers',
        },
        {
            icon: BiBlock,
            count: summaryData?.disabledPlatformNumbers,
            toolTip: 'Disabled Numbers',
        },
        {
            icon: AiOutlineInfoCircle,
            count: summaryData?.pendingPlatformNumbers,
            toolTip: 'Other Number Status',
        },
    ];

    return (
        <Container
            as="div"
            maxW="container.xl"
            px={0}
            aria-label="numbers section">
            <HStack justifyContent="space-between" marginBottom={5}>
                <Text sx={{ fontSize: '20', fontWeight: '500' }}>Numbers</Text>
                <Box
                    mb={4}
                    p={3}
                    borderRadius="md"
                    bg={darkMode ? '#lalala' : '#EEE'} // Replace with your actual table header color variable if different
                    display="flex"
                    alignItems="center">
                    <Text
                        sx={{
                            fontWeight: '500',
                        }}
                        mr={2}
                        minW="80px">
                        Summary
                    </Text>
                    <HStack
                        spacing={2}
                        wrap="wrap"
                        justifyContent="flex-end"
                        flex="1">
                        {summaryDetails.map((item, index) => (
                            <SummaryPill
                                key={index}
                                icon={item.icon}
                                count={item.count}
                                tooltip={item.toolTip}
                                sx={{
                                    marginRight:
                                        index < summaryDetails.length - 1
                                            ? '0px'
                                            : 0,
                                }}
                            />
                        ))}
                    </HStack>
                </Box>
            </HStack>
            <NumberTable
                platformList={platformList}
                expandedPlatformNumberIdRef={expandedPlatformNumberIdRef}
                expandedPlatformNumbersRef={expandedPlatformNumbersRef}
                setSummaryData={setSummaryData}
                menuData={menuData}
                countries={countries}
                isLoadingCountries={isLoadingCountries}
            />
            <ExistingNumbersDrawer
                isOpen={isBringExistingNumbersOpen}
                onClose={onBringExistingNumbersClose}
            />
            <RequestBlockDrawer
                isOpen={isRequestNumbersOpen}
                onClose={onRequestNumbersClose}
            />
            <AssociatedAccountDrawer
                isOpen={isTPMOpen}
                onClose={onTPMClose}
                currentStep={currentStep}
                associatedAccounts={associatedAccounts}
                isLoadingState={isLoading || isFetching}
                darkMode={darkMode}
            />
            <AvailableNumberBlocksDrawer
                isOpen={isAvailableNumberBlocksOpen}
                onClose={onAvailableNumberBlocksClose}
                {...numberBlocksTableProps}
            />
        </Container>
    );
}

export default Numbers;
